import { useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useToast } from '@/hooks/useToast'
import { captureError, addBreadcrumb } from '@/lib/sentry'

const SESSION_KEY = 'dextoro_session_start'
const CHECK_INTERVAL_MS = 30 * 1000 // 30 seconds
const WARNING_BEFORE_MS = 5 * 60 * 1000 // 5 minutes before expiry

export function startSession(): void {
  localStorage.setItem(SESSION_KEY, new Date().toISOString())
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_KEY)
}

export function useSessionTimeout(role: string | undefined): void {
  const { signOut } = useAuth()
  const navigate = useNavigate()
  const { toast } = useToast()
  const warningShown = useRef(false)

  useEffect(() => {
    if (!role) return

    const timeoutMs = role === 'investor' ? 4 * 60 * 60 * 1000 : 8 * 60 * 60 * 1000

    const interval = setInterval(() => {
      const sessionStart = localStorage.getItem(SESSION_KEY)
      if (!sessionStart) return

      const elapsed = Date.now() - new Date(sessionStart).getTime()

      if (elapsed >= timeoutMs) {
        clearSession()
        addBreadcrumb('Session timed out', 'auth', { elapsed, timeoutMs })
        signOut()
          .then(() => navigate('/login'))
          .catch((err) => {
            console.error('Failed to sign out on session timeout:', err)
            captureError(
              err instanceof Error ? err : new Error('Sign out failed on session timeout')
            )
            navigate('/login')
          })
        return
      }

      if (elapsed >= timeoutMs - WARNING_BEFORE_MS && !warningShown.current) {
        warningShown.current = true
        toast({
          title: 'Session Expiring',
          description: 'Your session will expire in 5 minutes.',
        })
      }
    }, CHECK_INTERVAL_MS)

    return () => clearInterval(interval)
  }, [role, signOut, navigate, toast])
}
